import React from 'react';
import type {
  OpenCanvasFeatureConfig,
  OpenCanvasStyleSheetOptions,
} from '@packages/systems/open-canvas/core';

import type {DesignerStores} from '@packages/systems/designer/flux';
import {
  selectCurrentBreakpointFrameId,
  selectMCCRenderedFrameIds,
  selectMCCSelectedFrameId,
  selectShouldUseMultiContextCanvas,
  mccNodeClickedInInactiveFrame,
} from '@packages/systems/multi-context-canvas/store';
import {MultiContextCanvasFramesRenderer} from '../MultiContextCanvasFramesRenderer';
import {MCCFrameSettings} from '../MCCFrameSettings';
import {
  MCC_FRAME_TOP_SCREEN_OFFSET,
  MCC_TOOLBAR_VISIBILITY_ZOOM_LIMIT,
} from './constants';

export {MCC_FRAME_TOP_SCREEN_OFFSET} from './constants';

/**
 * The default screen-space Y offset for the MCC viewport.
 * This value takes into account the height of the Frame's toolbar
 */
const DEFAULT_MCC_OFFSET_Y = -MCC_FRAME_TOP_SCREEN_OFFSET;
const MULTI_CONTEXT_CANVAS_STYLE_SHEET_OPTIONS: OpenCanvasStyleSheetOptions = {
  filterOpenCanvasBodyStyles: false,
  bodyElementStyleTarget: `div:where([data-wf-open-canvas-body-element-proxy])`,
};

/**
 * Configuration for the Multi Context Canvas feature to be used with the open canvas system
 */
export const multiContextCanvasOpenCanvasConfig: OpenCanvasFeatureConfig<DesignerStores> =
  {
    isActive: selectShouldUseMultiContextCanvas,
    selectors: {
      selectActiveFrameId: selectCurrentBreakpointFrameId,
      selectRenderedFrameIds: selectMCCRenderedFrameIds,
      selectSelectedFrameId: selectMCCSelectedFrameId,
      selectStyleSheetOptions: () => MULTI_CONTEXT_CANVAS_STYLE_SHEET_OPTIONS,
    },
    actions: {
      inactiveFrameNodeClicked: mccNodeClickedInInactiveFrame,
    },
    ui: {
      renderFrames: () => <MultiContextCanvasFramesRenderer />,
      renderFrameSettings: () => <MCCFrameSettings />,
    },
    viewport: {
      getDefaultPosition: (
        frameWidth,
        viewportWidth,
        frameOffsetX = 0,
        scale = 1
      ) => {
        if (frameWidth !== undefined && viewportWidth !== undefined) {
          return {
            x: frameOffsetX + frameWidth / 2 - viewportWidth / (2 * scale),
            y: DEFAULT_MCC_OFFSET_Y / scale,
            k: scale,
          };
        }
        return {
          x: frameOffsetX - 50 / scale,
          y: DEFAULT_MCC_OFFSET_Y / scale,
          k: scale,
        };
      },
      toolbarVisibilityZoomLimit: MCC_TOOLBAR_VISIBILITY_ZOOM_LIMIT,
    },
  };
